import React from 'react';
import clsx from 'clsx';
import { normalizeStatus, normalizeSeverity } from './violationMeta';

/**
 * Linear-style status glyph: an outlined circle that fills as work
 * progresses. Colour carries the state; the shape carries it again for
 * anyone who cannot rely on colour.
 */
export const StatusIcon: React.FC<{ status: string; className?: string }> = ({
  status,
  className,
}) => {
  const s = normalizeStatus(status);
  const common = clsx('h-3.5 w-3.5 shrink-0', className);
  switch (s) {
    case 'Pending':
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-zinc-400')} aria-hidden>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 2" />
        </svg>
      );
    case 'InProgress':
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-amber-400')} aria-hidden>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M7 3.5 A3.5 3.5 0 0 1 7 10.5 Z" fill="currentColor" />
        </svg>
      );
    case 'Escalated':
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-red-400')} aria-hidden>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M7 4v3.5M7 9.6v.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
    case 'Appealed':
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-violet-400')} aria-hidden>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
          <path d="M5.3 5.6a1.8 1.8 0 1 1 2.4 1.7c-.5.2-.7.5-.7 1v.2M7 9.9v.1" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
        </svg>
      );
    case 'Resolved':
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-sky-400')} aria-hidden>
          <circle cx="7" cy="7" r="6" fill="currentColor" />
          <path d="M4.5 7.2 6.3 9l3.2-3.6" fill="none" stroke="#08090A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 14 14" className={clsx(common, 'text-zinc-500')} aria-hidden>
          <circle cx="7" cy="7" r="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      );
  }
};

/** Linear's priority-style bars, used for severity: more bars, more severe. */
export const SeverityGlyph: React.FC<{ severity: string; className?: string }> = ({
  severity,
  className,
}) => {
  const level =
    { Minor: 1, Moderate: 2, Major: 3, Critical: 4 }[normalizeSeverity(severity)] ?? 0;
  if (level === 4) {
    return (
      <svg viewBox="0 0 14 14" className={clsx('h-3.5 w-3.5 shrink-0 text-red-400', className)} aria-hidden>
        <rect x="1" y="1" width="12" height="12" rx="3" fill="currentColor" />
        <path d="M7 3.8v3.9M7 9.8v.1" stroke="#08090A" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 14 14" className={clsx('h-3.5 w-3.5 shrink-0', className)} aria-hidden>
      {[0, 1, 2].map((i) => (
        <rect
          key={i}
          x={1.5 + i * 4}
          y={9 - i * 3}
          width="2.8"
          height={3.5 + i * 3}
          rx="0.8"
          className={i < level ? 'fill-fg-secondary' : 'fill-line-strong'}
        />
      ))}
    </svg>
  );
};
